import { Navigate } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { Card } from "@volca/ui/components/ui/card";
import { ErrorState } from "@volca/ui/components/ui/error-state";
import { PageContainer } from "@volca/ui/components/ui/page-components";
import { Skeleton } from "@volca/ui/components/ui/skeleton";
import { Stack } from "@volca/ui/components/ui/stack";
import { FEATURE_FLAG_NAMES } from "@/core/feature-flags/feature-flag-name";
import { useFeatureFlag } from "@/core/feature-flags/use-feature-flag";
import { RunDetail as JobCostingRunDetail } from "@/profitability/job-costing-run/run-detail";
import { useReportRun } from "@/profitability/query";
import { PROFITABILITY_PATH } from "@/profitability/routes";
import { RunDetail as LegacyRunDetail } from "@/profitability/run-detail";
import { reportError } from "@/profitability/utils";

export const VersionedRunDetail = ({
  businessId,
  runId,
}: {
  businessId: string;
  runId: string;
}) => {
  const { enabled: documentsOnly, isLoading: loadingFlag } = useFeatureFlag(
    FEATURE_FLAG_NAMES.profitability_documents_only,
  );
  const query = useReportRun(businessId, runId);
  const version = query.data?.workflow_version;
  const knownVersion = version === 1 || version === 2;
  if (loadingFlag || query.isPending || (query.isFetching && !knownVersion))
    return (
      <>
        <span role="status" className="sr-only">
          Loading report
        </span>
        <PageContainer variant="workspace" aria-busy>
          <Stack className="w-full" align="start" gap={1}>
            <Skeleton className="h-8.5 w-64" aria-hidden />
            <Skeleton className="h-5 w-96 max-w-full" aria-hidden />
          </Stack>
          <Card className="min-h-0 w-full flex-1 gap-0 py-0" />
        </PageContainer>
      </>
    );
  if (!query.data || !knownVersion)
    return (
      <PageContainer>
        <ErrorState
          title={
            query.data
              ? "This report could not be opened"
              : "This report could not be loaded"
          }
          description={
            query.data
              ? "Reload the report and try again."
              : reportError(query.error)
          }
        >
          <Button variant="secondary" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </ErrorState>
      </PageContainer>
    );
  if (version === 2 && !documentsOnly)
    return <Navigate to={PROFITABILITY_PATH} replace />;
  return query.data.workflow_version === 2 ? (
    <JobCostingRunDetail businessId={businessId} runId={runId} />
  ) : (
    <LegacyRunDetail businessId={businessId} runId={runId} />
  );
};
